import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { isTypingTarget } from '@/lib/keyboard'
import { G_JUMP_MAP } from '@/config/shortcuts'
import { useVisibleNavSections } from '@/config/navModel'
import { useUIStore } from '@/stores/useUIStore'

interface UseGlobalShortcutsOptions {
  /** Whether the command palette is currently open. */
  paletteOpen: boolean
  /** Toggle the command palette (Cmd/Ctrl+K). */
  togglePalette: () => void
  /** Open the shortcuts cheat-sheet (`?`). */
  openHelp: () => void
}

/** Time window (ms) to press the second key of a `g <key>` sequence. */
const G_SEQUENCE_TIMEOUT = 1000

/**
 * App-wide keyboard shortcuts, mounted once in the dashboard shell:
 *  - Cmd/Ctrl+K — toggle the command palette (works even inside inputs)
 *  - `g` then <key> — jump directly to a page (only if the user can access it)
 *  - `[` — toggle sidebar · `t` — toggle theme · `?` — open shortcuts help
 *
 * Single-key shortcuts are ignored while typing in a field. `f` (fullscreen) is
 * intentionally left to useFullscreenShortcut on pages that use it.
 */
export function useGlobalShortcuts({
  paletteOpen,
  togglePalette,
  openHelp,
}: UseGlobalShortcutsOptions): void {
  const navigate = useNavigate()
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)

  // Paths the current user is allowed to see — gates `g`-jumps so they never
  // navigate to a page the user lacks permission for.
  const sections = useVisibleNavSections()
  const visiblePaths = useMemo(() => {
    const set = new Set<string>()
    for (const section of sections) for (const item of section.items) set.add(item.to)
    return set
  }, [sections])

  // Keep the latest values in a ref so the keydown listener stays stable while
  // always seeing fresh state. Updated in an effect (never during render).
  const stateRef = useRef({ paletteOpen, togglePalette, openHelp, visiblePaths, navigate, setSidebarOpen })
  useEffect(() => {
    stateRef.current = { paletteOpen, togglePalette, openHelp, visiblePaths, navigate, setSidebarOpen }
  })

  const gPendingRef = useRef(false)
  const gTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const clearGPending = (): void => {
      gPendingRef.current = false
      if (gTimerRef.current) {
        clearTimeout(gTimerRef.current)
        gTimerRef.current = null
      }
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return
      const {
        paletteOpen: isPaletteOpen,
        togglePalette: toggle,
        openHelp: help,
        visiblePaths: paths,
        navigate: nav,
        setSidebarOpen: setSidebar,
      } = stateRef.current

      // Cmd/Ctrl+K — always active, even inside inputs.
      if ((event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        clearGPending()
        toggle()
        return
      }

      // Any other modifier combo, or typing in a field, cancels single-key handling.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        clearGPending()
        return
      }
      if (isTypingTarget(event.target)) return
      // Don't fire single-key shortcuts while the palette owns the keyboard.
      if (isPaletteOpen) return

      // Second key of a `g <key>` sequence.
      if (gPendingRef.current) {
        const target = G_JUMP_MAP[event.key.toLowerCase()]
        clearGPending()
        if (target && paths.has(target.to)) {
          event.preventDefault()
          nav(target.to)
        }
        return
      }

      // Start a `g` sequence.
      if (event.key.toLowerCase() === 'g') {
        gPendingRef.current = true
        if (gTimerRef.current) clearTimeout(gTimerRef.current)
        gTimerRef.current = setTimeout(clearGPending, G_SEQUENCE_TIMEOUT)
        return
      }

      // Standalone toggles.
      if (event.key === '[') {
        event.preventDefault()
        setSidebar(!useUIStore.getState().isSidebarOpen)
        return
      }
      if (event.key === 't' || event.key === 'T') {
        event.preventDefault()
        const { theme, setTheme } = useUIStore.getState()
        setTheme(theme === 'dark' ? 'light' : 'dark')
        return
      }
      if (event.key === '?') {
        event.preventDefault()
        help()
        return
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearGPending()
    }
  }, [])
}
